import { BaseService } from "./base.service";
import { TableFilterModel } from "models/TableFilterModel";
import { ITableNumberColumnFilter } from "models/TableNumberColumnFilter";
import { IBudgetAllocationSummaryGridDetail } from "models/Budget/BudgetAllocationSummaryDetail";
import {
	IBudgetAllocationNumber,
	IBudgetAllocationStatus,
	IBudgetGlCategory,
	IBudgetAllocationType,
	IBudgetTransactionType,
	IAddBudgetAllocationDetail,
	IUpdateBudgetAllocationDetail,
	IFinancialYearBudgetDetail,
	IBudgetAllocationStepHistory,
	IAddDeptAllocationLineDetail,
	IAllocationTabLineDetail,
	BudgetDetailByFinancialYear,
	BudgetAllocationCopyDetailModel,
	BudgetDeptAllocationAdjustment,
	IBudgetAllocationDeptAdjGridDetail,
	BudgetFinanceAllocationAdjustment,
	IBudgetAllocationFinanceAdjGridDetail,
	BudgetAllocationDeptAdjustmentDetail,
	BudgetAllocationFinanceAdjustmentDetail,
	DepartmentTransferDetailModel,
	ITransferProgGlAllocationDetail,
	IFinAllocationTransferGridDetail,
	IDeptAllocationTransferGridDetail,
	IGetDepartmentTransferDetail,
	FinanceTransferDetailModel,
	IGetFinanceTransferDetail,
	ITransferProgDeptAllocationDetail,
	IMonthlyAllocationDetail,
	IMonthlyAllocationAddDetail,
	BudgetAllocationHeaderDetail,
	DepartmentAllocationDetail,
	FinanceAllocationDetail,
	BudgetAllocationStepConfirmationDetail,
	IPreviousBudgetStepHistory,
	MasterBudgetGridDetail,
	IFinConsolidationStatusInput,
} from "models/Budget/BudgetAllocationDetail";
import { BudgetModulePhase } from "utility/enums/budget-allocation";
import {
	AddAllocationConfirmationRequest,
	ReadyStatusBudgetAllocationDetail,
} from "models/Budget/BudgetAllocationEmailConfirmation";

class BudgetAllocationServiceInternal extends BaseService {
	// Get budget allocation list for budget allocation summary grid
	getBudgetAllocationList = async (
		model: TableFilterModel,
		isDetailView = false
	): Promise<IBudgetAllocationSummaryGridDetail[]> =>
		this.callApi<IBudgetAllocationSummaryGridDetail[]>(
			`BudgetAllocation/GetBudgetAllocationList/${isDetailView}`,
			"POST",
			undefined,
			model
		);

	getBudgetAllocationStatusList = async (): Promise<
		IBudgetAllocationStatus[]
	> =>
		this.callApi<IBudgetAllocationStatus[]>(
			`BudgetAllocationStatus/GetAll`,
			"GET"
		);

	getBudgetAllocationNumberList = async (
		model: ITableNumberColumnFilter
	): Promise<IBudgetAllocationNumber[]> =>
		this.callApi<IBudgetAllocationNumber[]>(
			`BudgetAllocation/getBudgetAllocationNumberList`,
			"POST",
			undefined,
			model
		);

	getGlCategoryList = async (): Promise<IBudgetGlCategory[]> =>
		this.callApi<IBudgetGlCategory[]>(`GlCategory/GetAll`, "GET");

	getBudgetTransactionTypeList = async (): Promise<IBudgetTransactionType[]> =>
		this.callApi<IBudgetTransactionType[]>(
			`BudgetTransactionType/GetAll`,
			"GET"
		);

	getBudgetAllocationTypeList = async (): Promise<IBudgetAllocationType[]> =>
		this.callApi<IBudgetAllocationType[]>(`BudgetAllocationType/GetAll`, "GET");

	// Get financial year budgeting detail on click of add icon from summary grid
	getFinancialYearBudgetingDetail =
		async (): Promise<IFinancialYearBudgetDetail | null> =>
			this.callApi<IFinancialYearBudgetDetail | null>(
				`FinancialYear/GetFinancialYearBudgetingDetail`,
				"GET"
			);

	saveBudgetAllocationDetail = async (
		model: IAddBudgetAllocationDetail
	): Promise<{ budgetAllocationId: number; budgetAllocationNumber: string }> =>
		this.callApi<{
			budgetAllocationId: number;
			budgetAllocationNumber: string;
		}>(`BudgetAllocation/Save`, "POST", undefined, model);

	updateBudgetAllocationDetail = async (
		budgetAllocationId: number,
		model: IUpdateBudgetAllocationDetail
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocation/Update/${budgetAllocationId}`,
			"POST",
			undefined,
			model
		);

	getBudgetAllocationHeaderDetailById = async (
		budgetAllocationId: number
	): Promise<BudgetAllocationHeaderDetail> =>
		this.callApi<BudgetAllocationHeaderDetail>(
			`BudgetAllocation/GetBudgetAllocationHeaderDetail/${budgetAllocationId}`,
			"GET"
		);

	saveFinancialYearDetail = async (model: {
		financialYear1: string;
		startDate: Date;
		endDate: Date;
	}): Promise<boolean> =>
		this.callApi<boolean>(`FinancialYear/Save`, "POST", undefined, model);

	getBudgetAllocationDetailByErpDbCompanyId = async (
		erpDbCompanyId: number,
		financialYearId: number
	): Promise<{
		budgetAllocationDetail: BudgetAllocationHeaderDetail;
		isFinancialYearBudgeting: boolean;
	}> =>
		this.callApi<{
			budgetAllocationDetail: BudgetAllocationHeaderDetail;
			isFinancialYearBudgeting: boolean;
		}>(
			`BudgetAllocation/GetBudgetAllocationDetailByErpDbCompanyId/${erpDbCompanyId}/${financialYearId}`,
			"GET"
		);

	getFlyInFinanceAllocationList = async (
		budgetAllocationId: number,
		budgetAllocationStepId: number
	): Promise<FinanceAllocationDetail> =>
		this.callApi<FinanceAllocationDetail>(
			`BudgetAllocationLine/GetFlyInFinanceAllocationList/${budgetAllocationId}`,
			"GET",
			budgetAllocationStepId > 0
				? new URLSearchParams({
						budgetAllocationStepId: budgetAllocationStepId.toString(),
				  })
				: undefined
		);

	getFlyInDepartmentAllocationList = async (
		budgetAllocationId: number,
		budgetAllocationStepDepartmentId: number
	): Promise<DepartmentAllocationDetail> =>
		this.callApi<DepartmentAllocationDetail>(
			`BudgetAllocationLineDetail/GetFlyInDepartmentAllocationList/${budgetAllocationId}/${budgetAllocationStepDepartmentId}`,
			"GET"
		);

	submitFinanceAllocationBudget = async (
		budgetAllocationId: number,
		comment: string
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLine/SubmitFinanceAllocation/${budgetAllocationId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	submitDepartmentAllocation = async (
		budgetAllocationId: number,
		budgetAllocationStepDepartmentId: number,
		comment: string
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLineDetail/SubmitDepartmentAllocation/${budgetAllocationId}/${budgetAllocationStepDepartmentId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	submitFinanceConsolidateStep = async (
		budgetAllocationId: number,
		budgetAllocationStepId: number,
		comment: string
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLine/SubmitFinanceConsolidateStep/${budgetAllocationId}/${budgetAllocationStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	getBudgetAllocationStepList = async (
		budgetAllocationId: number
	): Promise<IBudgetAllocationStepHistory[]> =>
		this.callApi<IBudgetAllocationStepHistory[]>(
			`BudgetAllocationStep/GetBudgetAllocationStepList/${budgetAllocationId}`,
			"GET"
		);

	// To bind allocation tab grid detail in budget allocation window once if step no. >= 2
	getAllocationTabLineList = async (
		budgetAllocationId: number
	): Promise<IAllocationTabLineDetail[]> =>
		this.callApi<IAllocationTabLineDetail[]>(
			`BudgetAllocationLine/GetBudgetAllocationLineList/${budgetAllocationId}`,
			"GET"
		);

	// To bind master budget tab grid detail in budget allocation window if step no. >= 3
	getMasterBudgetTabLineList = async (
		budgetAllocationId: number
	): Promise<MasterBudgetGridDetail[]> =>
		this.callApi<MasterBudgetGridDetail[]>(
			`BudgetAllocationLineDetail/GetDepartmentAllocationBudgetList/${budgetAllocationId}`,
			"GET"
		);

	saveUpdateDeptAllocationLineDetail = async (
		budgetAllocationId: number,
		model: IAddDeptAllocationLineDetail[],
		deleteNonSelectedIds = true
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BudgetAllocationLineDetail/SaveUpdateAll/${budgetAllocationId}`,
			"POST",
			new URLSearchParams({
				deleteNonSelectedIds: deleteNonSelectedIds.toString(),
			}),
			model
		);

	// API call on approve, reject, task complete and change request buttons from Budget allocation detail page -  for finance allocation
	updateFinanceAllocationLineStatus = async (
		budgetAllocationId: number,
		budgetAllocationStepId: number,
		model: BudgetAllocationStepConfirmationDetail
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLine/UpdateFinanceAllocationLineStatus/${budgetAllocationId}/${budgetAllocationStepId}`,
			"POST",
			undefined,
			model
		);

	// API call on approve, reject, task complete and change request buttons from Budget allocation detail page -  for department allocation
	updateDepartmentAllocationLineStatus = async (
		budgetAllocationId: number,
		budgetAllocationStepDepartmentId: number,
		model: BudgetAllocationStepConfirmationDetail
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLineDetail/UpdateDepartmentAllocationLineStatus/${budgetAllocationId}/${budgetAllocationStepDepartmentId}`,
			"POST",
			undefined,
			model
		);

	// API call to bind step list dropdown for change request dialog
	getStepsBeforeCurrentStep = async (
		budgetAllocationStepId: number,
		budgetAllocationStepDepartmentId: number
	): Promise<IPreviousBudgetStepHistory[]> =>
		this.callApi<IPreviousBudgetStepHistory[]>(
			`BudgetAllocationStep/GetStepsBeforeCurrentStep/${budgetAllocationStepId}`,
			"GET",
			new URLSearchParams({
				budgetAllocationStepDepartmentId:
					budgetAllocationStepDepartmentId.toString(),
			})
		);

	// API call to re submit budget allocation detail for finance allocation after change request
	reSubmitFinanceAllocation = async (
		budgetAllocationId: number,
		currentStepBudgetAllocationStepId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLine/ReSubmitFinanceAllocation/${budgetAllocationId}/${currentStepBudgetAllocationStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	deleteBudgetAllocationDetail = async (
		budgetAllocationId: number,
		budgetAllocationStepId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocation/Delete/${budgetAllocationId}`,
			"DELETE",
			budgetAllocationStepId > 0
				? new URLSearchParams({
						budgetAllocationStepId: budgetAllocationStepId.toString(),
				  })
				: undefined
		);

	// Download letter from Allocation tab in budget allocation window
	downloadBudgetAllocationLetter = async (
		budgetAllocationId: number,
		departmentId: number,
		modulePhaseId = BudgetModulePhase.FinanceAllocation
	): Promise<string> =>
		this.callApi<string>(
			`BudgetAllocation/DownloadBudgetAllocationLetter/${budgetAllocationId}/${departmentId}/${modulePhaseId}`,
			"GET"
		);

	// Get budget allocation detail on edit (for email confirmation)
	getReadyStatusBudgetAllocationDetails = async (
		budgetAllocationId: string,
		budgetAllocationStepId: string,
		budgetAllocationStepDepartmentId: string
	): Promise<ReadyStatusBudgetAllocationDetail> =>
		this.callApi<ReadyStatusBudgetAllocationDetail>(
			`BudgetAllocation/GetReadyStatusBudgetAllocationDetails`,
			"POST",
			undefined,
			{
				Key: budgetAllocationId,
				Key_1: budgetAllocationStepId,
				Key_2: budgetAllocationStepDepartmentId,
			}
		);

	// Required to show missing department list (departments for which the logged in user are line manager) popup for department allocation (if not saved)
	getMissingFinancialAllocationDepartmentList = async (
		erpDbCompanyId: number,
		financialYearId: number
	): Promise<string[]> =>
		this.callApi<string[]>(
			`BudgetAllocationLine/GetMissingFinancialAllocationDepartmentList/${erpDbCompanyId}/${financialYearId}`,
			"GET"
		);

	// Required to show existing budget detail popup for department allocation (if not saved)
	getGlBudgetDetailByFinancialYearId = async (
		financialYearId: number,
		erpDbCompanyId: number
	): Promise<BudgetDetailByFinancialYear | null> =>
		this.callApi<BudgetDetailByFinancialYear | null>(
			`GlBudget/GetGlBudgetDetailByFinancialYearId/${financialYearId}/${erpDbCompanyId}`,
			"GET"
		);

	// NOTE: Required in Requisition Entry to restrict creating new requisition if the selected year is in budgeting state
	// Transaction Type: New Contract, New Panel, Contract Addendum/Extension and Panel Addendum/Extension
	validateContractPeriod = async (
		erpDbCompanyId: number,
		startDate: string,
		endDate: string,
		requisitionId: number
	): Promise<{
		isBudgetingYearExist: boolean;
		isChangeOrderExist: boolean;
		financialYears: string[];
	}> =>
		this.callApi<{
			isBudgetingYearExist: boolean;
			isChangeOrderExist: boolean;
			financialYears: string[];
		}>(
			`Requisition/ValidateContractPeriod/${erpDbCompanyId}/${startDate}/${endDate}`,
			"GET",
			new URLSearchParams({
				requisitionId: requisitionId.toString(),
			})
		);

	// Get Budget Allocation Detail on click of copy icon
	getBudgetAllocationCopyDetail = async (
		erpDbCompanyId: number,
		financialYearId: number
	): Promise<BudgetAllocationCopyDetailModel> =>
		this.callApi<BudgetAllocationCopyDetailModel>(
			`BudgetAllocation/GetBudgetAllocationCopyDetail/${erpDbCompanyId}/${financialYearId}`,
			"GET"
		);

	// START - Department Allocation Adjustment API
	saveUpdateBudgetAllocationLineDetailAdjustment = async (
		model: BudgetDeptAllocationAdjustment
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLineDetailAdjustment/SaveUpdate`,
			"POST",
			undefined,
			model
		);

	getBudgetAllocationLineDetailAdjustmentList = async (
		budgetAllocationLineDetailId: number
	): Promise<IBudgetAllocationDeptAdjGridDetail[]> =>
		this.callApi<IBudgetAllocationDeptAdjGridDetail[]>(
			`BudgetAllocationLineDetailAdjustment/GetBudgetAllocationLineDetailAdjustmentList/${budgetAllocationLineDetailId}`,
			"GET"
		);

	getBudgetAllocationLineDetailAdjustmentById = async (
		budgetAllocationLineDetailId: number
	): Promise<BudgetAllocationDeptAdjustmentDetail> =>
		this.callApi<BudgetAllocationDeptAdjustmentDetail>(
			`BudgetAllocationLineDetailAdjustment/GetBudgetAllocationLineDetailAdjustmentById/${budgetAllocationLineDetailId}`,
			"GET"
		);

	getDeptAdjustmentHistoryById = async (
		budgetAllocationLineDetailAdjustmentHistoryId: number
	): Promise<BudgetAllocationDeptAdjustmentDetail> =>
		this.callApi<BudgetAllocationDeptAdjustmentDetail>(
			`BudgetAllocationLineDetailAdjustmentHistory/GetAdjustmentHistoryById/${budgetAllocationLineDetailAdjustmentHistoryId}`,
			"GET"
		);

	deleteBudgetAllocationLineDetailAdjustment = async (
		budgetAllocationLineDetailAdjustmentId: number,
		budgetAllocationStepDepartmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLineDetailAdjustment/Delete/${budgetAllocationLineDetailAdjustmentId}/${budgetAllocationStepDepartmentId}`,
			"DELETE"
		);
	// END - Department Allocation Adjustment API

	// START - Finance Allocation Adjustment API
	saveUpdateBudgetAllocationLineAdjustment = async (
		model: BudgetFinanceAllocationAdjustment
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLineAdjustment/SaveUpdate`,
			"POST",
			undefined,
			model
		);

	getBudgetAllocationLineAdjustmentList = async (
		budgetAllocationLineId: number
	): Promise<IBudgetAllocationFinanceAdjGridDetail[]> =>
		this.callApi<IBudgetAllocationFinanceAdjGridDetail[]>(
			`BudgetAllocationLineAdjustment/GetBudgetAllocationLineAdjustmentList/${budgetAllocationLineId}`,
			"GET"
		);

	getBudgetAllocationLineAdjustmentById = async (
		budgetAllocationLineId: number
	): Promise<BudgetAllocationFinanceAdjustmentDetail> =>
		this.callApi<BudgetAllocationFinanceAdjustmentDetail>(
			`BudgetAllocationLineAdjustment/GetBudgetAllocationLineAdjustmentById/${budgetAllocationLineId}`,
			"GET"
		);

	getFinanceAdjustmentHistoryById = async (
		budgetAllocationLineAdjustmentHistoryId: number
	): Promise<BudgetAllocationFinanceAdjustmentDetail> =>
		this.callApi<BudgetAllocationFinanceAdjustmentDetail>(
			`BudgetAllocationLineAdjustmentHistory/GetAdjustmentHistoryById/${budgetAllocationLineAdjustmentHistoryId}`,
			"GET"
		);

	deleteBudgetAllocationLineAdjustment = async (
		budgetAllocationLineAdjustmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLineAdjustment/Delete/${budgetAllocationLineAdjustmentId} `,
			"DELETE"
		);
	// END - Finance Allocation Adjustment API

	// START - Department Allocation Transfer API
	getBudgetAllocationLineDetailTransferList = async (
		budgetAllocationLineDetailId: number
	): Promise<IDeptAllocationTransferGridDetail[]> =>
		this.callApi<IDeptAllocationTransferGridDetail[]>(
			`BudgetAllocationLineDetailTransfer/GetAllocationTransferList/${budgetAllocationLineDetailId}`,
			"GET"
		);

	saveBudgetAllocationLineDetailTransfer = async (
		model: DepartmentTransferDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLineDetailTransfer/SaveUpdate`,
			"POST",
			undefined,
			model
		);

	// Required to bind transfer detail
	getDeptAllocationTransferDetailById = async (
		headerBudgetAllocationLineDetailId: number,
		budgetAllocationLineDetailTransferHistoryId: number
	): Promise<IGetDepartmentTransferDetail> =>
		this.callApi<IGetDepartmentTransferDetail>(
			`BudgetAllocationLineDetailTransfer/GetAllocationTransferDetailById/${headerBudgetAllocationLineDetailId}`,
			"GET",
			new URLSearchParams({
				budgetAllocationLineDetailTransferHistoryId:
					budgetAllocationLineDetailTransferHistoryId.toString(),
			})
		);

	// Required to bind right section dropdown list
	getTransferProgrammeGLAllocationList = async (
		budgetAllocationId: number
	): Promise<ITransferProgGlAllocationDetail[]> =>
		this.callApi<ITransferProgGlAllocationDetail[]>(
			`BudgetAllocationLineDetailTransfer/GetTransferProgrammeGLAllocationList/${budgetAllocationId}`,
			"GET"
		);

	deleteBudgetAllocationLineDetailTransfer = async (
		budgetAllocationLineDetailTransferId: number,
		budgetAllocationStepDepartmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLineDetailTransfer/Delete/${budgetAllocationLineDetailTransferId}/${budgetAllocationStepDepartmentId}`,
			"DELETE"
		);
	// END - Department Allocation Transfer API

	// START - Finance Allocation Transfer API
	getBudgetAllocationLineTransferList = async (
		budgetAllocationLineId: number
	): Promise<IFinAllocationTransferGridDetail[]> =>
		this.callApi<IFinAllocationTransferGridDetail[]>(
			`BudgetAllocationLineTransfer/GetBudgetAllocationLineTransferList/${budgetAllocationLineId}`,
			"GET"
		);

	saveBudgetAllocationLineTransfer = async (
		model: FinanceTransferDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`BudgetAllocationLineTransfer/SaveUpdate`,
			"POST",
			undefined,
			model
		);

	// Required to bind left section detail & header detail
	getFinAllocationTransferDetailById = async (
		headerBudgetAllocationLineId: number,
		budgetAllocationLineTransferHistoryId: number
	): Promise<IGetFinanceTransferDetail> =>
		this.callApi<IGetFinanceTransferDetail>(
			`BudgetAllocationLineTransfer/GetAllocationTransferDetailById/${headerBudgetAllocationLineId}`,
			"GET",
			new URLSearchParams({
				budgetAllocationLineTransferHistoryId:
					budgetAllocationLineTransferHistoryId.toString(),
			})
		);

	// Required to bind right section dropdown list
	getTransferProgrammeDepartmentList = async (
		budgetAllocationId: number
	): Promise<ITransferProgDeptAllocationDetail[]> =>
		this.callApi<ITransferProgDeptAllocationDetail[]>(
			`BudgetAllocationLineTransfer/GetTransferProgrammeDepartmentList/${budgetAllocationId}`,
			"GET"
		);

	deleteBudgetAllocationLineTransfer = async (
		budgetAllocationLineTransferId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLineTransfer/Delete/${budgetAllocationLineTransferId} `,
			"DELETE"
		);
	// END - Finance Allocation Transfer API

	// START - Monthly Distribution API (for Department allocation only)
	// Get line detail for monthly allocation
	getMonthlyAllocationDetail = async (
		budgetAllocationLineDetailId: number
	): Promise<IMonthlyAllocationDetail> =>
		this.callApi<IMonthlyAllocationDetail>(
			`BudgetAllocationLineDetailMonthlyDistribution/GetBudgetAllocationLineDetailMonthlyDistributionList/${budgetAllocationLineDetailId}`,
			"GET"
		);

	// Save/Update line detail for monthly allocation
	saveUpdateMonthlyAllocation = async (
		model: IMonthlyAllocationAddDetail
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BudgetAllocationLineDetailMonthlyDistribution/SaveUpdateAll`,
			"POST",
			undefined,
			model
		);
	// END - Monthly Distribution API

	// Update budget allocation line status when status === "Finance consolidation"
	updateBudgetAllocationLineStatusById = async (
		financeAllocationStatusInputs: IFinConsolidationStatusInput[],
		budgetAllocationStepId: number,
		comment: string
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BudgetAllocationLine/UpdateBudgetAllocationLineStatusById/${budgetAllocationStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
				financeAllocationStatusInputs: financeAllocationStatusInputs,
			}
		);

	// Required for approval, rejection and task completion action via email for finance allocation
	updateFinanceAllocationLineViaEmail = async (
		budgetAllocationId: number,
		budgetAllocationStepId: number,
		model: AddAllocationConfirmationRequest
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLine/UpdateBudgetAllocationLineViaEmail/${budgetAllocationId}/${budgetAllocationStepId}`,
			"POST",
			undefined,
			model
		);

	// Required for approval, rejection and task completion action via email for department allocation
	updateDepartmentAllocationLineViaEmail = async (
		budgetAllocationId: number,
		budgetAllocationStepDepartmentId: number,
		model: AddAllocationConfirmationRequest
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BudgetAllocationLineDetail/UpdateDepartmentAllocationLineDetailViaEmail/${budgetAllocationId}/${budgetAllocationStepDepartmentId}`,
			"POST",
			undefined,
			model
		);

	importBudgetAllocationList = async (
		model: FormData
	): Promise<{ budgetAllocationId: number; messageList: string[] } | null> =>
		this.callApi<{ budgetAllocationId: number; messageList: string[] } | null>(
			`BudgetAllocation/ImportBudget`,
			"POST",
			undefined,
			model,
			false,
			true
		);
}

export const BudgetAllocationService = new BudgetAllocationServiceInternal();
